<!-- Mobile overlay backdrop -->
@if (isMobile && mobileSidebarVisible) {
  <div class="sidebar__overlay" (click)="closeMobileSidebar()"></div>
}

<nav class="sidebar" [class.sidebar--expanded]="isExpanded" [class.sidebar--collapsed]="isCollapsed" [class.sidebar--mobile]="isMobile && mobileSidebarVisible" [class.sidebar--mobile-hidden]="isMobile && !mobileSidebarVisible">

  <!-- Header -->
  <div class="sidebar__header">
    <!-- Mobile: brand + close button (same place as the topbar logo and burger) -->
    <div class="sidebar__mobile-top">
      <div class="sidebar__mobile-brand">
        <img [src]="mobileLogo" [alt]="brandName" class="sidebar__mobile-logo">
        <img [src]="mobileWordmark" [alt]="brandName" class="sidebar__mobile-wordmark">
      </div>
      <button type="button" class="sidebar__close" [attr.aria-label]="'common.a11y.closeMenu' | transloco" (click)="closeMobileSidebar()">
        <lucide-icon name="X" [size]="24" aria-hidden="true"></lucide-icon>
      </button>
    </div>

    <!-- Mobile: profile section -->
    <div class="sidebar__profile-mobile">
      <!-- Same sticker as the topbar avatar -->
      <img [src]="userSticker" alt="" class="sidebar__profile-sticker" />
      <div class="sidebar__profile-info">
        <span class="sidebar__profile-name">{{ userName }}</span>
      </div>
      <button type="button" class="sidebar__settings-btn" [class.active]="isProfileMenuOpen" aria-haspopup="menu" [attr.aria-expanded]="isProfileMenuOpen" [attr.aria-label]="'layout.profile.accountSettings' | transloco" (click)="toggleProfileMenu()">
        <lucide-icon [name]="isProfileMenuOpen ? 'X' : 'Settings'" [size]="20" aria-hidden="true"></lucide-icon>
      </button>
    </div>

    <!-- Mobile: profile dropdown -->
    @if (isProfileMenuOpen) {
      <div class="sidebar__profile-dropdown">
        <button class="sidebar__dropdown-item" (click)="openProfileModal()">
          <lucide-icon name="Settings" [size]="20"></lucide-icon>
          <span>{{ 'layout.profile.accountSettings' | transloco }}</span>
        </button>
        <button class="sidebar__dropdown-item" (click)="handleLogout()">
          <lucide-icon name="LogOut" [size]="20"></lucide-icon>
          <span>{{ 'layout.profile.logout' | transloco }}</span>
        </button>
      </div>
    }

    <!-- Desktop: logo -->
    <div class="sidebar__logo">
      <!-- Expanded: blue wordmark; collapsed: app icon -->
      @if (isCollapsed) {
        @if (brandLogo) {
          <img [src]="brandLogo" [alt]="brandName" class="sidebar__logo-img" (error)="brandLogo = null">
        }
      } @else {
        <img [src]="wordmark" [alt]="brandName" class="sidebar__logo-wordmark">
      }
    </div>
  </div>

  <!-- Main menu -->
  <ul class="sidebar__menu">
    @for (item of mainItems; track item.id) {
      <li class="sidebar__item" [class.sidebar__item--has-children]="!!item.children?.length">

        <!-- Item WITHOUT children + WITH routerLink: <a> -->
        @if (item.routerLink && !item.children?.length) {
          <a class="sidebar__link" [routerLink]="item.routerLink" routerLinkActive="sidebar__link--active" (click)="handleItemClick(item)" [class.sidebar__link--highlight]="item.highlight">
            @if (item.icon) {
              <lucide-icon [name]="item.icon" [size]="24" class="sidebar__icon"></lucide-icon>
            }
            <span class="sidebar__label" [title]="item.labelKey | transloco">{{ item.labelKey | transloco }}</span>
            <lucide-icon name="ChevronRight" [size]="20" class="sidebar__hover-arrow"></lucide-icon>
          </a>
          <!-- Simple tooltip (collapsed hover): just label -->
          <div class="sidebar__tooltip">
            <span>{{ item.labelKey | transloco }}</span>
          </div>
        }

        <!-- Item WITHOUT children + WITHOUT routerLink: <button> (used by UI kit) -->
        @if (!item.routerLink && !item.children?.length) {
          <button class="sidebar__link" (click)="handleItemClick(item)" [class.sidebar__link--highlight]="item.highlight">
            @if (item.icon) {
              <lucide-icon [name]="item.icon" [size]="24" class="sidebar__icon"></lucide-icon>
            }
            <span class="sidebar__label" [title]="item.labelKey | transloco">{{ item.labelKey | transloco }}</span>
            <lucide-icon name="ChevronRight" [size]="20" class="sidebar__hover-arrow"></lucide-icon>
          </button>
          <div class="sidebar__tooltip">
            <span>{{ item.labelKey | transloco }}</span>
          </div>
        }

        <!-- Item WITH children: <button> to toggle submenu -->
        @if (item.children?.length) {
          <button class="sidebar__link" (click)="handleItemClick(item)" [class.sidebar__link--highlight]="item.highlight" [class.sidebar__link--open]="isSubmenuExpanded(item)">
            @if (item.icon) {
              <lucide-icon [name]="item.icon" [size]="24" class="sidebar__icon"></lucide-icon>
            }
            <span class="sidebar__label" [title]="item.labelKey | transloco">{{ item.labelKey | transloco }}</span>
            <lucide-icon [name]="isSubmenuExpanded(item) ? 'ChevronUp' : 'ChevronDown'" [size]="20" class="sidebar__chevron"></lucide-icon>
          </button>

          <!-- Inline submenu: expanded + mobile (NOT collapsed) -->
          @if (isSubmenuExpanded(item) && !isCollapsed) {
            <ul class="sidebar__submenu">
              @for (child of item.children; track child.id) {
                <li>
                  <a class="sidebar__sublink" [routerLink]="child.routerLink" routerLinkActive="sidebar__sublink--active" (click)="handleItemClick(child)">
                    <span>{{ child.labelKey | transloco }}</span>
                  </a>
                </li>
              }
            </ul>
          }

          <!-- Floating submenu: collapsed only, opened by click -->
          @if (isSubmenuExpanded(item) && isCollapsed) {
            <div class="sidebar__floating-submenu">
              <span class="sidebar__floating-title">{{ item.labelKey | transloco }}</span>
              @for (child of item.children; track child.id) {
                <a class="sidebar__floating-link" [routerLink]="child.routerLink" routerLinkActive="sidebar__floating-link--active" (click)="expandedSubmenuId = null">
                  {{ child.labelKey | transloco }}
                </a>
              }
            </div>
          }
        }
      </li>
    }
  </ul>

  <!-- Footer -->
  <div class="sidebar__footer">
    <!-- Desktop: toggle button -->
    <button type="button" class="sidebar__toggle" [attr.aria-expanded]="isExpanded" [attr.aria-label]="(isExpanded ? 'layout.sidebar.closePanel' : 'common.a11y.openPanel') | transloco" (click)="toggleSidebar()">
      <lucide-icon [name]="isExpanded ? 'PanelLeftClose' : 'PanelLeftOpen'" [size]="24"></lucide-icon>
      <span class="sidebar__label">{{ 'layout.sidebar.closePanel' | transloco }}</span>
    </button>

    <!-- Mobile: logout -->
    <button class="sidebar__logout-mobile" (click)="handleLogout()">
      <lucide-icon name="LogOut" [size]="20"></lucide-icon>
      <span>{{ 'layout.profile.logout' | transloco }}</span>
    </button>
  </div>
</nav>
